import { InfoPage } from '@/components/info-page.js';
import { LoadingPage } from '@/components/loading-page.js';
import { getAnnouncements } from '@/lib/api-calls.js';
import { paginationRequestSchema } from '@/lib/pagination.js';
import { validateRouteRole } from '@/lib/routing.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import { formatInstant, isAirAdmin } from '@airreading/common';
import {
  Announcement,
  AnnouncementPlacementLabelMap,
  announcementPlacementSchema,
  announcementTypeSchema,
} from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense, useMemo } from 'react';
import { z } from 'zod';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { EntityTable } from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { AnnouncementTypeBadge } from './-components/annonuncement-type-badge.js';
import { Button } from '@/components/ui/button.js';
import { AnnouncementFormModal } from './-components/announcement-form-modal.js';
import { announcementPlacementOptions, announcementTypeOptions } from '@/lib/announcement-utils.js';
import { Pencil } from 'lucide-react';
import { Temporal } from 'temporal-polyfill';
import { useTimeZone } from '@/auth/useTimeZone.js';
import { coachesSectionTabs } from '../-components/tabs.js';
import { DeleteAnnouncement } from './-components/delete-announcement.js';

export const Route = createFileRoute('/_layout/teachers/announcement/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: Page,
  validateSearch: paginationRequestSchema.extend({
    query: z.string().optional(),
    placements: z.array(announcementPlacementSchema).optional(),
    types: z.array(announcementTypeSchema).optional(),
  }),
});

function Page() {
  return (
    <Suspense fallback={<LoadingPage />}>
      <PageDetail />
    </Suspense>
  );
}

function PageDetail() {
  const { timeZone } = useTimeZone();
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[nameQuery, activeNameQuery], setNameQuery],
      placements: [[placementIds, activePlacementIds], setPlacementIds],
      types: [[typeIds, activeTypeIds], setTypeIds],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'placements', 'types']);

  const {
    data: announcementsData,
    error,
    refetch,
    isPending,
  } = useQuery(
    getAnnouncements({
      pageIndex,
      pageSize,
      filters: {
        query: activeNameQuery,
        placements: activePlacementIds,
        types: activeTypeIds,
      },
    }),
  );

  const { announcements, total } = announcementsData || {};

  const columnHelper = createColumnHelper<Announcement>();

  const columns = [
    columnHelper.accessor('title', {
      id: 'title',
      header: 'Title',
      cell: ({ getValue }) => {
        return getValue() || '';
      },
    }),

    columnHelper.accessor('content', {
      id: 'content',
      header: 'Content',
      cell: ({ getValue }) => {
        return getValue();
      },
    }),

    columnHelper.accessor('placement', {
      id: 'placement',
      header: 'Placement',
      cell: ({ getValue }) => {
        return (
          <Badge variant="default" className="whitespace-nowrap">
            {AnnouncementPlacementLabelMap[getValue()]}
          </Badge>
        );
      },
    }),

    columnHelper.accessor('type', {
      id: 'type',
      header: 'Type',
      cell: ({ getValue }) => {
        return <AnnouncementTypeBadge type={getValue()} />;
      },
    }),

    columnHelper.accessor((row) => row, {
      header: 'Last edit',
      cell: ({ getValue }) => {
        const { createdAt, updatedAt } = getValue();

        if (!createdAt && !updatedAt) return null;

        const { formattedDateTime } = formatInstant(Temporal.Instant.from(updatedAt || createdAt), timeZone, {
          hour12: true,
        });
        return formattedDateTime;
      },
    }),

    columnHelper.accessor((row) => row, {
      id: 'actions',
      header: '',
      cell: ({ getValue }) => {
        const announcement = getValue();
        return (
          <div className="flex gap-2">
            <AnnouncementFormModal
              trigger={
                <Button variant={'outline'} size={'sm'} title="Edit">
                  <Pencil className="h-4 w-4" />
                </Button>
              }
              key={announcement.id}
              from={announcement}
            />
            <DeleteAnnouncement announcement={announcement} />
          </div>
        );
      },
    }),
  ];

  const table = useReactTable({
    data: announcements || [],
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    rowCount: total || 0,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    defaultColumn: {
      enableSorting: false,
    },
  });

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search by title or content"
          value={nameQuery ?? ''}
          onChange={(event) => {
            setNameQuery(event.target.value);
          }}
        />
        <TableFilterDropdown
          title="Placement"
          options={announcementPlacementOptions}
          selectedValues={placementIds ?? []}
          onChange={setPlacementIds}
        />
        <TableFilterDropdown
          title="Type"
          options={announcementTypeOptions}
          selectedValues={typeIds ?? []}
          onChange={setTypeIds}
        />
        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [nameQuery, setNameQuery, placementIds, setPlacementIds, typeIds, setTypeIds, resetFilters],
  );

  return (
    <InfoPage
      headerText="Teacher Announcements"
      headerActions={<AnnouncementFormModal trigger={<Button>Add Announcement</Button>} key="create" />}
      tabs={coachesSectionTabs()}
    >
      <EntityTable
        table={table}
        isLoading={isPending}
        showPagination
        emptyState={'No announcements found'}
        hasError={!!error}
        errorState={<TableErrorState refetchAction={refetch} />}
        filters={{ filterLeftComponents }}
      />
    </InfoPage>
  );
}
